import { Popover } from "@headlessui/react";
import { ArrowRightOnRectangleIcon, HeartIcon, HomeIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { CalendarDaysIcon, Cog6ToothIcon, EnvelopeIcon, LockClosedIcon } from "@heroicons/react/24/solid";
import { AuthContext } from "context/appContext";
import { Menu, ProfilClient, ProfilOwner, ProfilService } from "data/types";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { Fragment, useContext, useEffect, useState } from "react";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";
import { MdDashboardCustomize } from "react-icons/md";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logOut } from "redux/features/auth/authSlice";
import Avatar from "shared/Avatar/Avatar";
import { socket } from "utils/socket";
import { motion, AnimatePresence } from "framer-motion";

export default function AvatarDropdown() {
  const imagePng = "static-files/user.png";
  const userState = useAppSelector((state) => state?.auth?.user);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const authContext = useContext(AuthContext);
  const [profileImg, setProfileImg] = useState(imagePng);
  const [menu, setMenu] = useState<Menu[]>([]);
  const location = useLocation();
  const locationPath = location.pathname.startsWith("/signup");

  useEffect(() => {
    if (locationPath) {
      setMenu([]);
    }

    if (authContext.isAuthenticated && !locationPath) {
      switch (authContext.user?.role) {
        case "admin": {
          setMenu([
            {
              name: authContext.user?.profil ? authContext.user?.login : "ADMIN",
              href: "/",
              icon: UserCircleIcon,
            },
            {
              name: "Tableau de bord",
              href: "/dashboard",
              icon: MdDashboardCustomize,
            },
          ]);
          break;
        }
        case "creator": {
          setMenu([
            {
              name: authContext.user?.profil ? authContext.user?.login : "CREATOR",
              href: "/dashboard-creator",
              icon: UserCircleIcon,
            },
            {
              name: "Mes Articles",
              href: "/dashboard-creator",
              icon: UserCircleIcon,
            },
            {
              name: "Ajouter Article",
              href: "/dashboard/add-article",
              icon: CalendarDaysIcon,
            },
          ]);
          break;
        }
        case "client": {
          const profile = authContext.user && (authContext.user?.profil as ProfilClient);
          setProfileImg(profile?.client_url || imagePng);
          const clientMenu: Menu[] = [
            {
              name: "Mon compte",
              href: "/client/dashboard",
              icon: MdDashboardCustomize,
            },

            {
              name: "Profil",
              href: "/client",
              icon: UserCircleIcon,
            },
            ...(authContext?.user?.profil
              ? [
                  ...(profile?.validation !== "approuvé"
                    ? [
                        {
                          name: "Mes événements",
                          href: "/client/evenements",
                          icon: CalendarDaysIcon,
                        },
                      ]
                    : []),
                  ...(profile?.validation === "approuvé" && authContext?.user?.isCustomer !== true
                    ? [
                        {
                          name: "Mes réservations",
                          href: "/client/mes-reservations",
                          icon: CalendarDaysIcon,
                        },
                      ]
                    : []),

                  ...(profile?.validation === "approuvé" && authContext?.user?.isCustomer !== true
                    ? [
                        {
                          name: "Demande de réservations",
                          href: "/client/list-pending-reservations",
                          icon: CalendarDaysIcon,
                        },
                      ]
                    : []),

                  ...(profile?.validation === "approuvé"
                    ? [
                        {
                          name: "Mes espaces",
                          href: "/client/locations",
                          icon: HomeIcon,
                        },
                      ]
                    : []),

                  ...(profile?.validation === "en cours"
                    ? [
                        {
                          name: "Mes espaces",
                          href: "/client/demande",
                          icon: HomeIcon,
                        },
                      ]
                    : []),

                  {
                    name: "Espace favoris",
                    href: "/client/espaces-favoris",
                    icon: HeartIcon,
                  },
                  {
                    name: "Prestations favoris",
                    href: "/client/prestataires-favoris",
                    icon: HeartIcon,
                  },
                  {
                    name: "Mot de passe",
                    href: "/client-password",
                    icon: LockClosedIcon,
                  },
                ]
              : []),
          ];

          setMenu(clientMenu);
          break;
        }
        case "service": {
          const profile = authContext.user && (authContext.user?.profil as ProfilService);
          setProfileImg(profile?.service_url || imagePng);

          const serviceMenu: Menu[] = [
            {
              name: profile?.designation || "Profil",
              href: "/prestataire",
              icon: UserCircleIcon,
            },
            ...(authContext?.user?.profil
              ? [
                  {
                    name: "Mes prestations",
                    href: "/prestataire/services-list",
                    icon: HiOutlineSquare3Stack3D,
                  },
                  {
                    name: "Demandes de devis",
                    href: "/prestataire/pricing",
                    icon: EnvelopeIcon,
                  },
                ]
              : []),
          ];

          setMenu(serviceMenu);
          break;
        }
        case "owner": {
          const profile = authContext.user && (authContext.user?.profil as ProfilOwner);
          setProfileImg(profile?.owner_url || imagePng);

          const ownerMenu: Menu[] = [
            {
              name: profile?.companyName || `${profile?.lastName} ${profile?.name}`.trim() || "Profil",
              href: "/owner",
              icon: UserCircleIcon,
            },
            ...(authContext?.user?.profil
              ? [
                  {
                    name: "Mes espaces",
                    href: "/owner/locations",
                    icon: HomeIcon,
                  },
                  {
                    name: "Mes réservations",
                    href: "/owner/reservations",
                    icon: CalendarDaysIcon,
                  },
                  {
                    name: "Mot de passe",
                    href: "/owner-password",
                    icon: LockClosedIcon,
                  },
                  {
                    name: "Mes préferences",
                    href: `/owner-delete/${authContext.user?.id}`,
                    icon: Cog6ToothIcon,
                  },
                ]
              : []),
          ];

          setMenu(ownerMenu);

          break;
        }
      }
    }
  }, [authContext.user, locationPath]);

  const handleLogOut = () => {
    dispatch(logOut());
    navigate("/");
    socket.emit("send-disconnect", { userId: userState?.id });
  };

  return (
    <div className="AvatarDropdown">
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button className="group inline-flex items-center justify-center p-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900">
              <Avatar
                sizeClass="w-8 h-8 ring-2 ring-gray-100 dark:ring-gray-700"
                imgUrl={process.env.REACT_APP_CLOUD_FRONT + profileImg}
              />
            </Popover.Button>

            <AnimatePresence>
              {open && (
                <Popover.Panel
                  static
                  as={motion.div}
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="absolute z-50 w-screen max-w-xs mt-3 -right-4 sm:right-0"
                >
                  <div className="overflow-hidden bg-white dark:bg-gray-800 shadow-xl rounded-2xl ring-1 ring-black/5 dark:ring-white/10 backdrop-blur-sm">
                    {/* User Info Header */}
                    <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 border-b border-gray-100 dark:border-gray-700">
                      <div className="flex items-center space-x-3">
                        <Avatar
                          sizeClass="w-12 h-12 ring-2 ring-white dark:ring-gray-600 shadow-sm"
                          imgUrl={process.env.REACT_APP_CLOUD_FRONT + profileImg}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {authContext.user?.profil
                              ? authContext.user.role === "client"
                                ? `${(authContext.user.profil as ProfilClient)?.name || ""} ${(authContext.user.profil as ProfilClient)?.lastName || ""}`.trim() ||
                                  authContext.user.login
                                : authContext.user.role === "service"
                                  ? (authContext.user.profil as ProfilService)?.designation ||
                                    authContext.user.login
                                  : authContext.user.role === "owner"
                                    ? (authContext.user.profil as ProfilOwner)?.companyName ||
                                      `${(authContext.user.profil as ProfilOwner)?.lastName || ""} ${(authContext.user.profil as ProfilOwner)?.name || ""}`.trim() ||
                                      authContext.user.login
                                    : authContext.user.login
                              : authContext.user?.login || "Utilisateur"}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {authContext.user?.role === "client"
                              ? "Client"
                              : authContext.user?.role === "service"
                                ? "Prestataire"
                                : authContext.user?.role === "owner"
                                  ? "Propriétaire"
                                  : authContext.user?.role || "Utilisateur"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      {menu.map((item, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05, duration: 0.2 }}
                        >
                          <Link
                            to={item.href}
                            className="group flex items-center px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:text-emerald-700 dark:hover:text-emerald-400 transition-all duration-200"
                          >
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/30 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-all duration-200 mr-3">
                              <item.icon className="w-5 h-5" />
                            </div>
                            <span className="flex-1">{item.name}</span>
                            {item.name === "Mon compte" && (
                              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                            )}
                          </Link>
                        </motion.div>
                      ))}
                    </div>

                    {/* Separator */}
                    <div className="border-t border-gray-100 dark:border-gray-700"></div>

                    {/* Logout Section */}
                    <div className="py-2">
                      <motion.button
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: menu.length * 0.05, duration: 0.2 }}
                        onClick={handleLogOut}
                        className="group flex items-center w-full px-4 py-3 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                      >
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 group-hover:bg-red-200 dark:group-hover:bg-red-900/50 transition-all duration-200 mr-3">
                          <ArrowRightOnRectangleIcon className="w-5 h-5" />
                        </div>
                        <span className="flex-1">Se déconnecter</span>
                      </motion.button>
                    </div>
                  </div>
                </Popover.Panel>
              )}
            </AnimatePresence>
          </>
        )}
      </Popover>
    </div>
  );
}
